import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ChatPane from "@/components/ChatPane";
import DisclaimerRibbon from "@/components/DisclaimerRibbon";
import RadarScanner from "@/components/RadarScanner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import AdLockModal from "@/components/AdLockModal";
import useAdLock from "@/hooks/useAdLock";
import { ArrowLeft, MapPin, ShieldAlert, Loader2, Heart } from "lucide-react";
import { toast } from "sonner";

export default function BlindDate() {
  const nav = useNavigate();
  const { setPanel } = useAuth();
  const [phase, setPhase] = useState("locating"); // locating | denied | scanning | chat
  const [coords, setCoords] = useState(null);
  const [character, setCharacter] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scannedRef = useRef(false);
  const { adLocked, setAdLocked } = useAdLock();

  const requestLocation = useCallback(() => {
    setPhase("locating");
    if (!navigator.geolocation) { setPhase("scanning"); setCoords(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setPhase("scanning"); },
      () => setPhase("denied"),
      { timeout: 8000 }
    );
  }, []);

  useEffect(() => { requestLocation(); }, [requestLocation]);

  const onScanComplete = useCallback(async () => {
    if (scannedRef.current) return;
    scannedRef.current = true;
    try {
      const gender = localStorage.getItem("ah_orientation") === "male" ? "male" : "female";
      const { data } = await api.post("/blinddate/match", { ...(coords || {}), gender });
      setCharacter(data);
      setPhase("chat");
    } catch {
      toast.error("Match failed. Try again.");
      nav("/portal");
    }
  }, [coords, nav]);

  const send = async (retryMsg) => {
    const text = retryMsg ? retryMsg.content : draft.trim();
    if (!text || sending) return;
    if (adLocked) return setAdLocked(true);
    const tmpId = `tmp-${Date.now()}`;
    if (retryMsg) setMessages((m) => m.map((x) => x.id === retryMsg.id ? { ...x, id: tmpId, failed: false } : x));
    else { setDraft(""); setMessages((m) => [...m, { id: tmpId, sender: "user", content: text }]); }
    setSending(true);
    try {
      const { data } = await api.post("/chat", { character_id: character.id, content: text }, { timeout: 70000 });
      setMessages((m) => [...m, data.message]);
      setCharacter((c) => ({ ...c, trust_score: data.trust_score }));
      if (data.ad_locked) setAdLocked(true);
      if (data.migrated) {
        toast.success("Connection Trajectory Upgraded to Permanent Virtual Companion Profile status.");
        setTimeout(() => nav("/app"), 2500);
      }
    } catch (e) {
      setMessages((m) => m.map((x) => x.id === tmpId ? { ...x, failed: true } : x));
      if (e.response?.status === 423) { setAdLocked(true); return; }
      toast.error(e.response?.data?.detail || "Message failed to send. Tap retry.");
    }
    finally { setSending(false); }
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-ink">
      <DisclaimerRibbon />
      <div className="glass border-b border-white/10 px-4 py-2.5 flex items-center gap-3">
        <button data-testid="blinddate-back" onClick={() => nav("/portal")}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" /> Portal
        </button>
        <button data-testid="switch-companion" onClick={async () => { try { await setPanel("companion"); } catch {} nav("/app"); }}
          className="ml-auto flex items-center gap-1.5 text-sm text-brand hover:text-brand-hover transition-colors">
          <Heart className="w-4 h-4" fill="currentColor" /> Companion Hub
        </button>
      </div>

      <div className="flex-1 min-h-0">
        {(phase === "locating" || phase === "scanning") && (
          <div className="h-full flex flex-col items-center justify-center p-6 text-center">
            {phase === "locating" ? (
              <>
                <Loader2 className="w-8 h-8 text-brand animate-spin mb-4" />
                <p className="text-muted-foreground">Requesting location access…</p>
              </>
            ) : (
              <>
                <RadarScanner onComplete={onScanComplete} />
                <p className="text-cyan-300 mt-4 font-heading tracking-wide animate-pulse">Scanning regional grid networks…</p>
              </>
            )}
          </div>
        )}

        {phase === "denied" && (
          <div className="h-full flex flex-col items-center justify-center p-6 text-center max-w-md mx-auto">
            <div className="w-16 h-16 rounded-3xl bg-brand/20 flex items-center justify-center mb-5">
              <ShieldAlert className="w-8 h-8 text-brand" />
            </div>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Location permission parameters are required to match secure terminal grid networks.
              Please enable location routing to proceed.
            </p>
            <div className="flex gap-3">
              <Button data-testid="blinddate-retry" onClick={requestLocation}
                className="h-12 px-6 rounded-full bg-brand hover:bg-brand-hover text-white transition-colors">
                <MapPin className="w-4 h-4 mr-2" /> Enable Location
              </Button>
              <Button variant="ghost" data-testid="blinddate-skip" onClick={() => { setCoords(null); setPhase("scanning"); }}
                className="h-12 px-6 rounded-full text-muted-foreground">Skip</Button>
            </div>
          </div>
        )}

        {phase === "chat" && character && (
          <div className="h-full flex flex-col">
            <div className="px-4 sm:px-6 py-2 text-xs text-muted-foreground flex items-center gap-1.5 border-b border-white/5">
              <MapPin className="w-3 h-3 text-cyan-300" /> {character.name}, {character.age} • near {character.landmark}
            </div>
            <div className="flex-1 min-h-0">
              <ChatPane character={character} messages={messages} sending={sending}
                draft={draft} setDraft={setDraft} onSend={() => send()} onRetry={send}
                variant="blinddate" />
            </div>
          </div>
        )}
      </div>
      <AdLockModal open={adLocked} onUnlocked={() => setAdLocked(false)} />
    </div>
  );
}
